/**
 * Générateur — Résoudre des problèmes de multiplication décimale
 * Attendu ID : 615 | Niveau : 6ème | Domaine : NC
 *
 * Types générés :
 *   - probmult_qcm_unique    (problème contextualisé avec 4 choix)
 *   - probmult_texte         (problème contextualisé, saisie directe)
 *
 * Règles dures de la fiche cluster 8 (section ID 615) :
 *   - 3 sous-familles obligatoires : prix simple / prix au kg / économie.
 *   - Répartition équilibrée (random uniforme sur les 3 sous-familles).
 *   - Prix décimaux réalistes (2 décimales), quantités entières 2..20.
 *   - Résultat lisible (≤ 4 décimales ; idéalement 2 décimales pour euros).
 *   - Distracteurs : addition au lieu de ×, mauvais placement de virgule
 *     (×10 trop long), résultat sans virgule, oubli d'une partie.
 *
 * Mécanismes-clés testés :
 *   - Modélisation du problème (choisir la bonne opération)
 *   - Calcul d'un produit décimal × entier
 *   - Interprétation de l'unité (€)
 *   - Contrôle par ordre de grandeur
 *
 * Variantes estimées : > 1500
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie,
} from '../../../utils';

// ─── Constantes ───
const ATTENDU_ID = 615;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_QCM = 'Résoudre un problème de multiplication décimale';
const L_SAISIE = 'Taper la réponse d\'un problème de multiplication décimale';

// ─── Helpers numériques (string-based, exact) ───

const decCount = (s) => {
    const [, dec = ''] = String(s).split(',');
    return dec.length;
};

/** Produit exact de a × b (strings en écriture française).
 *  Valide tant que les facteurs strippés restent ≪ Number.MAX_SAFE_INTEGER (~9×10¹⁵). */
const mulDec = (a, b) => {
    const da = decCount(a);
    const db = decCount(b);
    const ai = parseInt(String(a).replace(/[\s,]/g, ''), 10);
    const bi = parseInt(String(b).replace(/[\s,]/g, ''), 10);
    const prod = ai * bi;
    const totalDec = da + db;
    if (totalDec === 0) return String(prod);
    let p = String(prod);
    while (p.length <= totalDec) p = '0' + p;
    const intPart = p.slice(0, p.length - totalDec);
    const decPart = p.slice(p.length - totalDec);
    const intTrim = intPart.replace(/^0+/, '') || '0';
    const decTrim = decPart.replace(/0+$/, '');
    if (decTrim === '') return intTrim;
    return `${intTrim},${decTrim}`;
};

/** Somme de deux décimaux en écriture française (string). */
const addDec = (a, b) => {
    const da = decCount(a);
    const db = decCount(b);
    const D = Math.max(da, db);
    const ai = parseInt(String(a).replace(/[\s,]/g, '') + '0'.repeat(D - da), 10);
    const bi = parseInt(String(b).replace(/[\s,]/g, '') + '0'.repeat(D - db), 10);
    const sum = ai + bi;
    if (D === 0) return String(sum);
    let s = String(sum);
    while (s.length <= D) s = '0' + s;
    const intPart = s.slice(0, s.length - D);
    const decPart = s.slice(s.length - D).replace(/0+$/, '');
    const intTrim = intPart.replace(/^0+/, '') || '0';
    return decPart === '' ? intTrim : `${intTrim},${decPart}`;
};

/** Différence positive de deux décimaux (string). */
const subDec = (a, b) => {
    const da = decCount(a);
    const db = decCount(b);
    const D = Math.max(da, db);
    const ai = parseInt(String(a).replace(/[\s,]/g, '') + '0'.repeat(D - da), 10);
    const bi = parseInt(String(b).replace(/[\s,]/g, '') + '0'.repeat(D - db), 10);
    const diff = ai - bi;
    const sign = diff < 0 ? '-' : '';
    const absDiff = Math.abs(diff);
    if (D === 0) return sign + String(absDiff);
    let s = String(absDiff);
    while (s.length <= D) s = '0' + s;
    const intPart = s.slice(0, s.length - D);
    const decPart = s.slice(s.length - D).replace(/0+$/, '');
    const intTrim = intPart.replace(/^0+/, '') || '0';
    return sign + (decPart === '' ? intTrim : `${intTrim},${decPart}`);
};

/** Écriture canonique (revue 2026-08-21, choix prof) : retirer les zéros de
 *  fin (« 19,20 » → « 19,2 », « 5,0 » → « 5 ») plutôt que padder à 2 décimales
 *  — écriture simple type « 5 € », appliquée à TOUS les choix. */
const formatEur = (s) => {
    const str = String(s);
    if (!str.includes(',')) return str;
    let t = str.replace(/0+$/, '');
    if (t.endsWith(',')) t = t.slice(0, -1);
    return t;
};

/** Retire le séparateur virgule (simulation "résultat sans virgule"). */
const dropComma = (s) => s.replace(',', '');

/**
 * Produit BRUT des écritures entières sous-jacentes (somme des chiffres
 * décimaux des facteurs implicite). Correspond à l'erreur didactique
 * « poser le calcul et oublier de replacer la virgule ».
 * Ex : rawIntProduct('2,40','8') → '1920' ; rawIntProduct('13,24','9') → '11916'.
 */
const rawIntProduct = (a, b) => {
    const ai = parseInt(String(a).replace(/[\s,]/g, ''), 10);
    const bi = parseInt(String(b).replace(/[\s,]/g, ''), 10);
    return String(ai * bi);
};

/** Déplace la virgule d'une position à gauche (ex : 119,16 → 11,916). */
const shiftLeftOne = (s) => {
    if (!s.includes(',')) return `${s.slice(0, -1)},${s.slice(-1)}`;
    const [intPart, decPart] = s.split(',');
    if (intPart.length === 1) return `0,${intPart}${decPart}`;
    return `${intPart.slice(0, -1)},${intPart.slice(-1)}${decPart}`;
};

// ─── Pools de contextes ───

const PRENOMS = ['Lila', 'Noé', 'Maya', 'Hugo', 'Léa', 'Tom', 'Zoé', 'Yanis', 'Chloé', 'Adam', 'Jade', 'Raphaël'];

// Sous-famille A : prix simple (prix unitaire × quantité entière)
// Chaque item : [nom singulier, nom pluriel, prix en euros string, déterminant article, verbe d'achat]
const POOL_PRIX_SIMPLE = [
    { sing: 'un pantalon',    plu: 'pantalons',    prix: '13,24', verb: 'achète' },
    { sing: 'un t-shirt',     plu: 't-shirts',     prix: '8,75',  verb: 'achète' },
    { sing: 'un cahier',      plu: 'cahiers',      prix: '2,40',  verb: 'achète' },
    { sing: 'un stylo',       plu: 'stylos',       prix: '1,15',  verb: 'achète' },
    { sing: 'un livre de poche', plu: 'livres de poche', prix: '6,90', verb: 'achète' },
    { sing: 'un classeur',    plu: 'classeurs',    prix: '4,25',  verb: 'achète' },
    { sing: 'une calculatrice', plu: 'calculatrices', prix: '15,60', verb: 'achète' },
    { sing: 'une trousse',    plu: 'trousses',     prix: '3,45',  verb: 'achète' },
    { sing: 'une BD',         plu: 'BD',           prix: '12,50', verb: 'achète' },
    { sing: 'une place de cinéma', plu: 'places de cinéma', prix: '9,80', verb: 'réserve' },
];

// Sous-famille B : prix au kg (masse × prix/kg)
// Masses toutes entières 2..10 (pour garder un résultat lisible avec 2 décimales)
const POOL_PRIX_KG = [
    { produit: 'les tomates', unite: 'kilogramme', abrev: 'kg', prix: '6,14' },
    { produit: 'les pommes', unite: 'kilogramme', abrev: 'kg', prix: '3,20' },
    { produit: 'la viande hachée', unite: 'kilogramme', abrev: 'kg', prix: '12,50' },
    { produit: 'le poisson', unite: 'kilogramme', abrev: 'kg', prix: '18,40' },
    { produit: 'les oranges', unite: 'kilogramme', abrev: 'kg', prix: '2,75' },
    { produit: 'les fraises', unite: 'kilogramme', abrev: 'kg', prix: '5,80' },
    { produit: 'le fromage', unite: 'kilogramme', abrev: 'kg', prix: '14,60' },
    { produit: 'les bananes', unite: 'kilogramme', abrev: 'kg', prix: '1,95' },
    { produit: 'les carottes', unite: 'kilogramme', abrev: 'kg', prix: '2,35' },
    { produit: 'les pommes de terre', unite: 'kilogramme', abrev: 'kg', prix: '1,75' },
];

// Sous-famille C : économie (différence de prix × quantité)
// Chaque item : { produit singulier, pluriel, prixA, prixB (prixB > prixA), verbe }
const POOL_ECONOMIE = [
    { sing: 'un cahier', plu: 'cahiers', prixA: '2,45', prixB: '2,80' },
    { sing: 'un stylo', plu: 'stylos', prixA: '1,20', prixB: '1,55' },
    { sing: 'un yaourt', plu: 'yaourts', prixA: '0,35', prixB: '0,50' },
    { sing: 'une baguette', plu: 'baguettes', prixA: '0,95', prixB: '1,20' },
    { sing: 'une bouteille d\'eau', plu: 'bouteilles d\'eau', prixA: '0,45', prixB: '0,70' },
    { sing: 'un paquet de biscuits', plu: 'paquets de biscuits', prixA: '2,15', prixB: '2,50' },
    { sing: 'un jus de fruit', plu: 'jus de fruit', prixA: '1,80', prixB: '2,25' },
    { sing: 'une part de pizza', plu: 'parts de pizza', prixA: '3,40', prixB: '3,75' },
];

// ─── Assemblage d'un problème (texte, bonne réponse, distracteurs) ───

/** Construit un contexte « prix simple » : texte HTML, bonne_reponse (string), distracteurs. */
const buildPrixSimple = () => {
    const item = randomChoice(POOL_PRIX_SIMPLE);
    const prenom = randomChoice(PRENOMS);
    const qte = randInt(3, 15);
    const prix = item.prix;
    const total = mulDec(prix, String(qte));
    const totalFmt = formatEur(total);

    const texte = `<p>${prenom} ${item.verb} ${qte} ${item.plu} à ${prix} € pièce. Combien va-t-${
        ['Noé', 'Hugo', 'Tom', 'Yanis', 'Adam', 'Raphaël'].includes(prenom) ? 'il' : 'elle'
    } payer au total ?</p>`;

    // Distracteurs typiques
    const distAdd = addDec(prix, String(qte));       // addition au lieu de ×
    const distShift = shiftLeftOne(total);           // virgule décalée (×10 trop loin)
    const distNoComma = rawIntProduct(prix, String(qte)); // oubli de replacer la virgule
    // Normalisation (€)
    const bonneReponse = totalFmt;
    const distracteurs = [
        formatEur(distAdd) + ' €',
        formatEur(distShift) + ' €',
        formatEur(distNoComma) + ' €',
    ];
    return {
        texte,
        bonne_reponse_fmt: bonneReponse + ' €',
        bonne_reponse_num: total,
        suffixe: '€',
        distracteurs,
    };
};

/** Construit un contexte « prix au kg ». */
const buildPrixKg = () => {
    const item = randomChoice(POOL_PRIX_KG);
    const masse = randInt(2, 10);
    const prix = item.prix;
    const total = mulDec(prix, String(masse));
    const totalFmt = formatEur(total);

    // Accord sujet : "la viande / le poisson / le fromage" = singulier → "coûte"
    // Sinon (les tomates, les pommes, …) = pluriel → "coûtent"
    const estSingulier = /^(la |le |l')/.test(item.produit);
    const verbeSujet = estSingulier ? 'coûte' : 'coûtent';
    const texte = `<p>Au marché, ${item.produit} ${verbeSujet} ${prix} € le ${item.unite}. Combien coûtent ${masse} ${item.abrev} de ${item.produit.replace(/^(les |la |le |l')/, '')} ?</p>`;

    const distAdd = addDec(prix, String(masse));
    const distShift = shiftLeftOne(total);
    const distNoComma = rawIntProduct(prix, String(masse));

    const bonneReponse = totalFmt;
    const distracteurs = [
        formatEur(distAdd) + ' €',
        formatEur(distShift) + ' €',
        formatEur(distNoComma) + ' €',
    ];
    return {
        texte,
        bonne_reponse_fmt: bonneReponse + ' €',
        bonne_reponse_num: total,
        suffixe: '€',
        distracteurs,
    };
};

/** Construit un contexte « économie » (comparer 2 prix unitaires, multiplier la différence). */
const buildEconomie = () => {
    const item = randomChoice(POOL_ECONOMIE);
    const prenom = randomChoice(PRENOMS);
    const qte = randInt(4, 15);

    const prixA = item.prixA; // magasin A (moins cher)
    const prixB = item.prixB; // magasin B (plus cher)
    const diffUnit = subDec(prixB, prixA);
    const eco = mulDec(diffUnit, String(qte));
    const ecoFmt = formatEur(eco);

    const totalA = mulDec(prixA, String(qte));
    const totalB = mulDec(prixB, String(qte));

    const pronom = ['Noé', 'Hugo', 'Tom', 'Yanis', 'Adam', 'Raphaël'].includes(prenom) ? 'il' : 'elle';
    const texte = `<p>${prenom} veut acheter ${qte} ${item.plu}. Au magasin A, ${item.sing} coûte ${prixA} €. Au magasin B, ${item.sing} coûte ${prixB} €. Combien économise-t-${pronom} en achetant au magasin A plutôt qu'au magasin B ?</p>`;

    // Distracteurs : différence unitaire seule (oubli du ×qte), total A, total B
    const distracteurs = [
        formatEur(diffUnit) + ' €',     // oubli de multiplier par qte
        formatEur(totalA) + ' €',       // confusion : prix total A
        formatEur(totalB) + ' €',       // confusion : prix total B
    ];
    // Garde anti-fuite (revue 2026-08-21) : si l'économie tombe juste (2 € pour
    // 0,25 × 8), les trois distracteurs restent décimaux et la bonne réponse
    // serait le seul choix sans virgule. On remplace alors le total B par
    // l'économie ×10 (erreur de virgule), entière elle aussi.
    if (!eco.includes(',')) {
        distracteurs[2] = formatEur(mulDec(eco, '10')) + ' €';
    }

    return {
        texte,
        bonne_reponse_fmt: ecoFmt + ' €',
        bonne_reponse_num: eco,
        suffixe: '€',
        distracteurs,
    };
};

// Anti-collision : garantir distracteurs tous distincts de la bonne réponse
// et pas de doublons entre eux.
const cleanDistracteurs = (bonne, dists, fallbackProvider) => {
    const set = new Set([bonne]);
    const clean = [];
    for (const d of dists) {
        if (!set.has(d)) {
            set.add(d);
            clean.push(d);
        }
    }
    // S'il manque des distracteurs, piocher dans le fallback
    let guard = 0;
    while (clean.length < 3 && guard < 20) {
        const fb = fallbackProvider(set.size);
        if (!set.has(fb)) {
            set.add(fb);
            clean.push(fb);
        }
        guard++;
    }
    return clean.slice(0, 3);
};

const BUILDERS = {
    prix_simple: buildPrixSimple,
    prix_kg: buildPrixKg,
    economie: buildEconomie,
};

const pickSousFamille = () => {
    // Répartition uniforme sur 3 sous-familles (≥ 33% chacune en moyenne)
    return randomChoice(['prix_simple', 'prix_kg', 'economie']);
};

// ─── Générateurs de type ───

// Anti-répétition immédiate (revue 2026-08-21, recette standard).
let derniereSignatureQcm615 = null;

// Rappel méthodo affiché sous l'énoncé (demande prof 2026-08-28).
const RAPPEL_BROUILLON = '<p class="italic text-slate-400">✏️ Pense à poser la multiplication sur ton cahier ou au brouillon avant de répondre.</p>';

const genProbMultQcm = () => {
    let payload = construireProbMultQcm();
    for (let t = 0; t < 8 && payload.texte === derniereSignatureQcm615; t++) {
        payload = construireProbMultQcm();
    }
    derniereSignatureQcm615 = payload.texte;
    return payload;
};

const construireProbMultQcm = () => {
    const sousFamille = pickSousFamille();
    const ctx = BUILDERS[sousFamille]();

    const bonne = ctx.bonne_reponse_fmt;
    const distracteurs = cleanDistracteurs(bonne, ctx.distracteurs, (i) => {
        // Fallback : variation ×10 / ÷10 (en écriture canonique, sinon la
        // concaténation « +0 » recrée la valeur de la bonne réponse)
        const alt = i % 2 === 0
            ? formatEur(shiftLeftOne(ctx.bonne_reponse_num)) + ' €'
            : formatEur(ctx.bonne_reponse_num + '0') + ' €';
        return alt;
    });
    const choix = shuffle([bonne, ...distracteurs]);

    const payload = makeQcm(
        choix,
        bonne,
        ctx.texte + RAPPEL_BROUILLON,
        opts(L_QCM),
    );
    // Calculatrice RETIRÉE (décision prof 2026-08-28), sur les deux formats.
    return payload;
};

// Anti-répétition immédiate (revue 2026-08-21, recette standard).
let derniereSignatureSaisie615 = null;

const genProbMultSaisie = () => {
    let payload = construireProbMultSaisie();
    for (let t = 0; t < 8 && payload.texte === derniereSignatureSaisie615; t++) {
        payload = construireProbMultSaisie();
    }
    derniereSignatureSaisie615 = payload.texte;
    return payload;
};

const construireProbMultSaisie = () => {
    const sousFamille = pickSousFamille();
    const ctx = BUILDERS[sousFamille]();

    // Pour la saisie : on accepte la réponse numérique sans suffixe, avec suffixe € affiché.
    const payload = makeSaisie(
        ctx.bonne_reponse_num,
        ctx.texte + RAPPEL_BROUILLON,
        { ...opts(L_SAISIE), strict: false, suffixe: '€' },
    );
    // Calculatrice RETIRÉE (décision prof 2026-08-28), comme sur le QCM.
    return payload;
};

// ─── Registre ───
const GENERATORS = {
    probmult_qcm_unique: genProbMultQcm,
    probmult_texte: genProbMultSaisie,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', texte: 'Saisie',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'probmult', label: 'Résoudre un problème de multiplication décimale', formatsList: buildFormats('probmult') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
